/* ═══════════════════════════════════════════════════════════════
   body-layout — the ONE layout brain for SPEC BODY paragraphs.

   Sibling of `zone-layout.ts` (which owns L/C/R header/footer zones).
   Before this module, body layout was derived independently three
   times — word-engine (the .docx truth), the formatted preview, and
   the editor canvas — so "preview doesn't match export" was a
   structural guarantee, not a bug. This module makes every body
   decision ONCE, from the forma:

     · the CSI numbering scheme (which %N pattern each level prints)
     · the computed paragraph number for every block
     · indents in Word's model (wrap edge + hanging/first-line)
     · space before/after, keep rules, contextual spacing, alignment
     · the run properties a level imposes (bold/caps/italic/…)

   Consumers turn those decisions into their own output:
     · word-engine.ts     → OOXML (styles.xml + numbering.xml)
     · the Designer canvas → CSS
     · export preview / diff rendering → CSS

   Pure TypeScript: no Node, no DOM, no measurement. Anything that
   needs to MEASURE (pagination) injects its own measurer, exactly
   like zone-layout does.
   ═══════════════════════════════════════════════════════════════ */

import type { ContentBlock, LevelStyle, StandardConfig } from './types';

// ─── Units ───

/** 1 inch = 1440 twips. */
export const inToTwip = (inches: number): number => Math.round(inches * 1440);
/** 1 pt = 20 twips. */
export const ptToTwip = (pt: number): number => Math.round(pt * 20);
/** CSS px at the canvas' 96-DPI paper scale. */
export const inToPx = (inches: number): number => inches * 96;
export const ptToPx = (pt: number): number => (pt * 96) / 72;

// ─── The CSI numbering scheme ───

/** One level of the Word numbering definition (abstractNum ▸ lvl). */
export interface CsiLevelFormat {
  /** Word numFmt: decimal | decimalZero | upperLetter | lowerLetter | upperRoman | lowerRoman */
  numFmt: string;
  /** Word lvlText, e.g. "PART %1 - ", "%1.%2", "(%8)". */
  lvlText: string;
  /** Word suff: what separates the number from the text. null = Word's default (tab). */
  suff: string | null;
  /** Level 2 only: pad the counter to two digits ("1.01" vs "1.1"). */
  zeroPad?: boolean;
}

/** The out-of-the-box CSI MasterFormat scheme (PART 1 - / 1.01 / A. / 1. / a. / …). */
export const CSI_LEVELS_DEFAULT: CsiLevelFormat[] = [
  { numFmt: 'decimal', lvlText: 'PART %1 - ', suff: 'space' },
  { numFmt: 'decimal', lvlText: '%1.%2', suff: null, zeroPad: true },
  { numFmt: 'upperLetter', lvlText: '%3.', suff: null },
  { numFmt: 'decimal', lvlText: '%4.', suff: null },
  { numFmt: 'lowerLetter', lvlText: '%5.', suff: null },
  { numFmt: 'decimal', lvlText: '%6)', suff: null },
  { numFmt: 'lowerLetter', lvlText: '%7)', suff: null },
  { numFmt: 'decimal', lvlText: '(%8)', suff: null },
  { numFmt: 'lowerLetter', lvlText: '(%9)', suff: null },
];

/** `special.numberFormats[i]` id → the level format it selects. */
const FORMAT_OVERRIDES: Record<string, Partial<CsiLevelFormat>> = {
  part_dash: { numFmt: 'decimal', lvlText: 'PART %1 - ', suff: 'space' },
  part_space: { numFmt: 'decimal', lvlText: 'PART %1 ', suff: 'space' },
  '1.01': { numFmt: 'decimal', lvlText: '%1.%2', suff: null, zeroPad: true },
  '1.1': { numFmt: 'decimal', lvlText: '%1.%2', suff: null, zeroPad: false },
};

/** Swappable levels 6-9: format id → lvlText for that level index. */
const LVL_TEXT_MAP: Record<number, Record<string, string>> = {
  5: { '1)': '%6)', '(1)': '(%6)' },
  6: { 'a)': '%7)', '(a)': '(%7)' },
  7: { '(1)_8': '(%8)', '1)_8': '%8)', '(1)': '(%8)', '1)': '%8)' },
  8: { '(a)_9': '(%9)', 'a)_9': '%9)', '(a)': '(%9)', 'a)': '%9)' },
};

/**
 * Resolve the forma's chosen numbering scheme. Identical logic to the
 * engine's former private `getCSILevels()` — this is now its only home.
 */
export function getCsiLevelFormats(config: StandardConfig | null | undefined): CsiLevelFormat[] {
  const nf = config?.special?.numberFormats;
  if (!nf) return CSI_LEVELS_DEFAULT.map((l) => ({ ...l }));

  const levels: CsiLevelFormat[] = CSI_LEVELS_DEFAULT.map((l) => ({ ...l }));
  for (let i = 0; i < nf.length && i < 9; i++) {
    const fid = nf[i];
    if (!fid) continue;
    if (i <= 1) {
      const ov = FORMAT_OVERRIDES[fid];
      if (ov) levels[i] = { ...levels[i], ...ov };
    } else if (i >= 5 && LVL_TEXT_MAP[i]) {
      const lt = LVL_TEXT_MAP[i][fid];
      if (lt) {
        levels[i] = {
          numFmt: fid.includes('a') ? 'lowerLetter' : 'decimal',
          lvlText: lt,
          suff: null,
        };
      }
    }
  }
  return levels;
}

// ─── Counter rendering (Word's numFmt) ───

const ROMAN: Array<[number, string]> = [
  [1000, 'm'],
  [900, 'cm'],
  [500, 'd'],
  [400, 'cd'],
  [100, 'c'],
  [90, 'xc'],
  [50, 'l'],
  [40, 'xl'],
  [10, 'x'],
  [9, 'ix'],
  [5, 'v'],
  [4, 'iv'],
  [1, 'i'],
];

function toRoman(n: number): string {
  if (n <= 0) return '';
  let out = '';
  let rest = n;
  for (const [v, s] of ROMAN) {
    while (rest >= v) {
      out += s;
      rest -= v;
    }
  }
  return out;
}

/** Word's a…z, aa…az, ba… sequence (never runs out, unlike a 26-item clamp). */
function toAlpha(n: number): string {
  if (n <= 0) return '';
  const letter = String.fromCharCode(97 + ((n - 1) % 26));
  return letter.repeat(Math.floor((n - 1) / 26) + 1);
}

/** Render one counter with a Word numFmt. */
export function formatCounter(n: number, numFmt: string): string {
  switch (numFmt) {
    case 'decimalZero':
      return String(n).padStart(2, '0');
    case 'upperLetter':
      return toAlpha(n).toUpperCase();
    case 'lowerLetter':
      return toAlpha(n);
    case 'upperRoman':
      return toRoman(n).toUpperCase();
    case 'lowerRoman':
      return toRoman(n);
    case 'none':
      return '';
    default:
      return String(n);
  }
}

/** The effective numFmt for a level, folding in level 2's zeroPad switch. */
function effectiveNumFmt(fmt: CsiLevelFormat): string {
  if (fmt.zeroPad === true && fmt.numFmt === 'decimal') return 'decimalZero';
  return fmt.numFmt;
}

// ─── Numbering a document ───

export interface NumberedRef {
  /** The block this number belongs to. */
  id: string;
  /** 1-9. */
  level: number;
  /**
   * The printed paragraph number, trailing whitespace trimmed
   * ("PART 1 -", "1.01", "A.", "(a)"). Empty for continuation blocks
   * and for levels whose scheme prints nothing.
   */
  prefix: string;
  /** What separates the number from the text: Word's `suff`. */
  suffix: 'tab' | 'space' | 'nothing';
  /** Counter values 1-9 at this paragraph (1-based; 0 = not yet reached). */
  counters: number[];
}

/**
 * Is this block part of the printed document? Excluded and un-included
 * blocks never reach the .docx, so they must never consume a number —
 * that mismatch is why the old preview drifted from the export.
 */
export function isRenderable(b: Pick<ContentBlock, 'included' | 'excluded'>): boolean {
  return b.included !== false && b.excluded !== true;
}

/**
 * Compute the CSI number for every block, exactly as Word will when the
 * forma's numbering definition is applied to the exported paragraphs.
 *
 * Rules (all mirror Word):
 *  - a paragraph at level L increments counter L and resets L+1…9
 *  - `continuation` blocks share the previous paragraph's number and are
 *    not counted (they carry no numPr in the export)
 *  - non-renderable blocks (excluded / not included) are skipped entirely
 *  - each `%N` in the level's lvlText renders counter N with LEVEL N's numFmt
 */
export function computeNumbering(
  blocks: ContentBlock[],
  config: StandardConfig | null | undefined
): NumberedRef[] {
  const formats = getCsiLevelFormats(config);
  const counters = [0, 0, 0, 0, 0, 0, 0, 0, 0];
  const out: NumberedRef[] = [];

  for (const b of blocks) {
    const level = Math.max(1, Math.min(9, b.level || 1));
    if (!isRenderable(b)) {
      out.push({ id: b.id, level, prefix: '', suffix: 'nothing', counters: counters.slice() });
      continue;
    }
    if (b.continuation) {
      out.push({ id: b.id, level, prefix: '', suffix: 'nothing', counters: counters.slice() });
      continue;
    }
    counters[level - 1]++;
    for (let i = level; i < 9; i++) counters[i] = 0;

    const fmt = formats[level - 1] || CSI_LEVELS_DEFAULT[level - 1];
    const prefix = fmt.lvlText
      .replace(/%([1-9])/g, (_m, d: string) => {
        const idx = Number(d) - 1;
        const src = formats[idx] || CSI_LEVELS_DEFAULT[idx];
        return formatCounter(counters[idx], effectiveNumFmt(src));
      })
      .replace(/\s+$/, '');

    out.push({
      id: b.id,
      level,
      prefix,
      suffix: fmt.suff === 'space' ? 'space' : fmt.suff === 'nothing' ? 'nothing' : 'tab',
      counters: counters.slice(),
    });
  }
  return out;
}

/** Convenience: id → NumberedRef. */
export function numberingById(refs: NumberedRef[]): Map<string, NumberedRef> {
  return new Map(refs.map((r) => [r.id, r]));
}

// ─── Paragraph layout per level ───

/** Every layout decision one spec level imposes on its paragraphs. */
export interface BodyLevelLayout {
  /** 1-9. */
  level: number;

  // ── Indentation, in Word's model ──
  /**
   * The WRAP EDGE (Word's `w:ind w:left`) in twips: where wrapped lines and
   * the paragraph text begin. = indent + hangingIndent, floored at 0.
   */
  leftTwips: number;
  /** Word's `w:hanging` in twips (≥ 0). Mutually exclusive with firstLineTwips. */
  hangingTwips: number;
  /** Word's `w:firstLine` in twips (≥ 0) — set when the forma dragged the
   *  first line PAST the numbering (a negative hanging indent). */
  firstLineTwips: number;

  // ── Vertical rhythm ──
  spaceBeforeTwips: number;
  spaceAfterTwips: number;
  /** `w:contextualSpacing` — drop space-before between same-style paragraphs. */
  contextualSpacing: boolean;
  keepWithNext: boolean;
  keepLinesTogether: boolean;

  // ── Alignment ──
  /** 'left' is Word's default and emits no <w:jc>. */
  justify: 'left' | 'center' | 'right' | 'justify';

  // ── Run properties the level imposes ──
  bold: boolean;
  italic: boolean;
  underline: boolean;
  allCaps: boolean;
  /** Text case applied at compile time (Word styles can't express title/sentence). */
  textCase?: 'upper' | 'title' | 'sentence';
  /** Hex without '#'. '000000' means "inherit body colour". */
  fontColor: string;

  /** The numbering scheme for this level. */
  numbering: CsiLevelFormat;
}

const DEFAULT_LEVEL: LevelStyle = {
  level: 1,
  bold: false,
  allCaps: false,
  italic: false,
  underline: false,
  fontColor: '000000',
  indent: 0,
  hangingIndent: 0,
  spaceBefore: 0,
  spaceAfter: 0,
  keepWithNext: false,
  keepLinesTogether: false,
  noSpaceSameStyle: false,
};

/**
 * Resolve ONE level's layout from the forma.
 *
 * The indent maths is the engine's law, stated once:
 *   Word's `w:left` is the WRAP edge; the numbered first line starts at
 *   `left - hanging`. So a forma's "indent" (where text starts) plus its
 *   "hangingIndent" (the number column width) is the wrap edge, and the
 *   hanging pulls the first line back to the indent. A NEGATIVE hanging
 *   means the designer dragged the first line past the numbering — Word
 *   expresses that as `w:firstLine`, never a negative `w:hanging`.
 */
export function resolveLevelLayout(
  config: StandardConfig | null | undefined,
  levelIndex: number
): BodyLevelLayout {
  const lvl: LevelStyle = (config?.levels || [])[levelIndex] || {
    ...DEFAULT_LEVEL,
    level: levelIndex + 1,
  };
  const formats = getCsiLevelFormats(config);

  const indentTw = inToTwip(lvl.indent || 0);
  const hangTw = inToTwip(lvl.hangingIndent || 0);
  const leftTwips = Math.max(0, indentTw + hangTw);

  return {
    level: levelIndex + 1,
    leftTwips,
    hangingTwips: hangTw >= 0 ? hangTw : 0,
    firstLineTwips: hangTw < 0 ? -hangTw : 0,
    spaceBeforeTwips: ptToTwip(lvl.spaceBefore || 0),
    spaceAfterTwips: ptToTwip(lvl.spaceAfter || 0),
    contextualSpacing: !!lvl.noSpaceSameStyle,
    keepWithNext: !!lvl.keepWithNext,
    keepLinesTogether: !!lvl.keepLinesTogether,
    justify: lvl.justify || 'left',
    bold: !!lvl.bold,
    italic: !!lvl.italic,
    underline: !!lvl.underline,
    allCaps: !!lvl.allCaps,
    textCase: lvl.textCase,
    fontColor: lvl.fontColor || '000000',
    numbering: formats[levelIndex] || CSI_LEVELS_DEFAULT[levelIndex],
  };
}

/** All nine levels resolved. Index 0 = level 1. */
export function resolveBodyLayout(config: StandardConfig | null | undefined): BodyLevelLayout[] {
  return Array.from({ length: 9 }, (_, i) => resolveLevelLayout(config, i));
}

// ─── Page geometry (shared by canvas + export preview) ───

export interface BodyPageGeometry {
  pageWidthIn: number;
  pageHeightIn: number;
  marginTopIn: number;
  marginBottomIn: number;
  marginLeftIn: number;
  marginRightIn: number;
  /** Usable text column width. */
  contentWidthIn: number;
  /** Usable text column height (between the margins). */
  contentHeightIn: number;
  bodyFont: string;
  bodyFontSizePt: number;
  lineSpacing: number;
  widowOrphan: boolean;
  firstPageDifferent: boolean;
  oddEvenDifferent: boolean;
}

export function resolvePageGeometry(config: StandardConfig | null | undefined): BodyPageGeometry {
  const d = (config?.docSettings || {}) as Partial<StandardConfig['docSettings']>;
  const pageWidthIn = d.pageWidth || 8.5;
  const pageHeightIn = d.pageHeight || 11;
  const marginTopIn = d.marginTop ?? 1;
  const marginBottomIn = d.marginBottom ?? 1;
  const marginLeftIn = d.marginLeft ?? 1;
  const marginRightIn = d.marginRight ?? 1;
  return {
    pageWidthIn,
    pageHeightIn,
    marginTopIn,
    marginBottomIn,
    marginLeftIn,
    marginRightIn,
    contentWidthIn: Math.max(0.5, pageWidthIn - marginLeftIn - marginRightIn),
    contentHeightIn: Math.max(0.5, pageHeightIn - marginTopIn - marginBottomIn),
    bodyFont: d.bodyFont || 'Times New Roman',
    bodyFontSizePt: d.bodyFontSize || 12,
    lineSpacing: d.lineSpacing || 1,
    widowOrphan: d.widowOrphan !== false,
    firstPageDifferent: !!d.firstPageDifferent,
    oddEvenDifferent: !!d.oddEvenDifferent,
  };
}

// ─── CSS emission (the Designer canvas + any DOM preview) ───

/** The subset of CSS this module dictates. Callers merge it into their own. */
export interface BodyParagraphCss {
  paddingLeft: string;
  textIndent: string;
  marginTop: string;
  marginBottom: string;
  textAlign: 'left' | 'center' | 'right' | 'justify';
  fontWeight: number;
  fontStyle: 'normal' | 'italic';
  textDecoration: 'none' | 'underline';
  textTransform: 'none' | 'uppercase';
  color?: string;
}

/**
 * Turn one level's layout decisions into paragraph CSS, in Word's model:
 * `padding-left` is the WRAP edge and `text-indent` is negative by the
 * hanging amount, so the number sits in its own column and wrapped lines
 * align under the text — exactly what the .docx does.
 *
 * `sameStyleAsPrev` implements Word's contextual spacing.
 */
export function bodyParagraphCss(
  layout: BodyLevelLayout,
  opts: { sameStyleAsPrev?: boolean; sameStyleAsNext?: boolean; continuation?: boolean } = {}
): BodyParagraphCss {
  const leftIn = layout.leftTwips / 1440;
  const hangIn = layout.hangingTwips / 1440;
  const firstIn = layout.firstLineTwips / 1440;
  const suppressBefore = layout.contextualSpacing && !!opts.sameStyleAsPrev;
  // Word's contextualSpacing zeroes BOTH sides of a same-style boundary —
  // the preview used to keep the previous paragraph's space-after, showing a
  // gap the .docx doesn't print.
  const suppressAfter = layout.contextualSpacing && !!opts.sameStyleAsNext;
  // A continuation paragraph carries no number, so its first line starts at
  // the wrap edge like every other line.
  const textIndentIn = opts.continuation ? 0 : firstIn - hangIn;
  return {
    paddingLeft: `${inToPx(leftIn)}px`,
    textIndent: `${inToPx(textIndentIn)}px`,
    marginTop: `${suppressBefore ? 0 : ptToPx(layout.spaceBeforeTwips / 20)}px`,
    marginBottom: `${suppressAfter ? 0 : ptToPx(layout.spaceAfterTwips / 20)}px`,
    textAlign: layout.justify,
    fontWeight: layout.bold ? 700 : 400,
    fontStyle: layout.italic ? 'italic' : 'normal',
    textDecoration: layout.underline ? 'underline' : 'none',
    textTransform: layout.allCaps ? 'uppercase' : 'none',
    ...(layout.fontColor && layout.fontColor !== '000000' ? { color: `#${layout.fontColor}` } : {}),
  };
}

/**
 * Width reserved for the number column of a level, in CSS px. Word gives the
 * number `hanging` worth of room and tabs to the wrap edge; when a forma
 * leaves no hanging, Word falls to the next default tab stop (0.5").
 */
export function numberColumnPx(layout: BodyLevelLayout): number {
  return inToPx((layout.hangingTwips || inToTwip(0.5)) / 1440);
}

/** Apply a level's `textCase` to a block's text (Word styles can't). */
export function applyTextCase(text: string, layout: BodyLevelLayout): string {
  const mode = layout.textCase ?? (layout.allCaps ? 'upper' : undefined);
  if (!mode || !text) return text;
  if (mode === 'upper') return text.toUpperCase();
  if (mode === 'sentence') {
    const lower = text.toLowerCase();
    return lower.charAt(0).toUpperCase() + lower.slice(1);
  }
  // title
  return text.toLowerCase().replace(/\b[a-z]/g, (c) => c.toUpperCase());
}
